// ✅ Ruta: src/pages/Bots.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/Toast';
import PageHeader from '../components/PageHeader';
import { db, auth } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from 'firebase/firestore';
import { canCreateBotLocally, getBotCreationBlockedMessage } from '../utils/planHelpers';
import { useOrganization } from '../context/OrganizationContext';
import { useTranslation } from 'react-i18next';

const FUNCTIONS_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-sistema-bks.cloudfunctions.net';

const Bots = () => {
  const { t } = useTranslation('bots');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { context, loading: contextLoading } = useOrganization();
  const [bots, setBots] = useState([]);
  const [botsWithPlaybooks, setBotsWithPlaybooks] = useState({});
  const [clientData, setClientData] = useState(null);
  const uid = auth.currentUser?.uid;

  // Permission checks based on organization context
  const canCreate = context.member?.role === 'owner' || context.member?.permissions?.canCreateBots;
  const canDelete = context.member?.role === 'owner' || context.member?.permissions?.canDeleteBots;

  // Cargar datos del cliente/organización (para validar límites)
  useEffect(() => {
    if (!uid || contextLoading) return;

    const loadClientData = async () => {
      let clientRef;
      if (context.orgId) {
        clientRef = doc(db, 'organizations', context.orgId);
      } else {
        clientRef = doc(db, 'clientes', uid);
      }
      const clientSnap = await getDoc(clientRef);
      if (clientSnap.exists()) {
        setClientData({ id: clientSnap.id, ...clientSnap.data() });
      }
    };

    loadClientData();
  }, [uid, contextLoading, context.mode, context.orgId]);

  // Cargar bots avanzados con sus playbooks
  useEffect(() => {
    if (!uid || contextLoading) return;

    console.log('🤖 Bots.js loading data:', {
      uid,
      contextMode: context.mode,
      contextOrgId: context.orgId,
      contextLoading,
      timestamp: new Date().toISOString()
    });

    const cargarBots = async () => {
      // Query based on organization context
      let col;
      if (context.mode === 'organization' && context.orgId) {
        // Org mode: query from organizations/{orgId}/bots
        col = collection(db, 'organizations', context.orgId, 'bots');
      } else {
        // Personal mode: query from clientes/{uid}/bots
        col = collection(db, 'clientes', uid, 'bots');
      }

      const snapshot = await getDocs(col);
      const lista = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setBots(lista);

      // Self-heal: sync usage.botsCount with actual bot count
      if (clientData && (clientData.usage?.botsCount || 0) !== lista.length) {
        try {
          let usageRef;
          if (context.mode === 'organization' && context.orgId) {
            usageRef = doc(db, 'organizations', context.orgId);
          } else {
            usageRef = doc(db, 'clientes', uid);
          }
          await updateDoc(usageRef, { 'usage.botsCount': lista.length });
          console.log(`🔄 Synced botsCount: ${clientData.usage?.botsCount} → ${lista.length}`);
        } catch (e) {
          console.warn('⚠️ Failed to sync botsCount:', e);
        }
      }

      // Cargar playbooks para cada bot
      const playbooksData = {};
      for (const bot of lista) {
        let playbooksCol;
        if (context.mode === 'organization' && context.orgId) {
          playbooksCol = collection(db, 'organizations', context.orgId, 'bots', bot.id, 'playbooks');
        } else {
          playbooksCol = collection(db, 'clientes', uid, 'bots', bot.id, 'playbooks');
        }
        const playbooksSnap = await getDocs(playbooksCol);
        playbooksData[bot.id] = playbooksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      setBotsWithPlaybooks(playbooksData);
    };

    cargarBots();
  }, [uid, contextLoading, context]);

  // Verificar si un bot avanzado está completamente configurado
  const isBotConfigComplete = (bot) => {
    const config = bot.config || {};
    const playbooks = botsWithPlaybooks[bot.id] || [];

    const hasName = !!(config.name && config.name.trim());
    const hasPurpose = !!(config.purpose && config.purpose.trim());
    const hasFunnel = playbooks.some(p => p.states && Object.keys(p.states).length > 0);
    const hasTone = !!config.tone;
    const hasLanguage = !!config.language;

    return hasName && hasPurpose && hasFunnel && hasTone && hasLanguage;
  };

  // Activar/desactivar bot avanzado
  const toggleBotAvanzado = async (botId, currentlyActive) => {
    try {
      let botRef;
      if (context.mode === 'organization' && context.orgId) {
        botRef = doc(db, 'organizations', context.orgId, 'bots', botId);
      } else {
        botRef = doc(db, 'clientes', uid, 'bots', botId);
      }
      await updateDoc(botRef, {
        autoReply: !currentlyActive
      });

      // Recargar bots
      let col;
      if (context.mode === 'organization' && context.orgId) {
        col = collection(db, 'organizations', context.orgId, 'bots');
      } else {
        col = collection(db, 'clientes', uid, 'bots');
      }
      const snapshot = await getDocs(col);
      const lista = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setBots(lista);
    } catch (error) {
      console.error('Error toggling bot avanzado:', error);
      toast.error(t('list.errorToggle'));
    }
  };

  const eliminarBot = async (id) => {
    // Validate permissions
    if (!canDelete) {
      toast.error(t('list.noPermissionDelete'));
      return;
    }

    if (!window.confirm(t('list.confirmDelete'))) return;

    // EL BORRADO LO HACE EL SERVIDOR (D3c). El decremento de `usage.botsCount`
    // se hacía aquí, con `remainingBots.length` — un absoluto sobre el estado
    // local—, y al cerrar ese campo en las reglas se quedaba sin dueño. Ahora
    // `deleteBot` borra y decrementa en la misma transacción.
    try {
      const idToken = await auth.currentUser.getIdToken();
      const payload = { botId: id };
      if (context.mode === 'organization' && context.orgId) {
        payload.organizationId = context.orgId;
      }
      const r = await fetch(`${FUNCTIONS_URL}/deleteBot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        toast.error(data.message || t('list.errorDelete'));
        return;
      }
      setBots(bots.filter((bot) => bot.id !== id));
    } catch (error) {
      console.error('Error eliminando bot:', error);
      toast.error(t('list.errorDelete'));
    }
  };

  const crearBotAvanzado = async () => {
    // Validate permissions
    if (!canCreate) {
      toast.error(t('list.noPermissionCreateOrg'));
      return;
    }

    // Validate bot creation limits
    if (!clientData) {
      toast.error(t('list.errorLoadAccount'));
      return;
    }

    if (!canCreateBotLocally(clientData, bots.length)) {
      const message = getBotCreationBlockedMessage(clientData, bots.length);
      const confirmUpgrade = window.confirm(
        `${message}\n\n${t('list.confirmUpgrade')}`
      );
      if (confirmUpgrade) {
        navigate('/addon');
      }
      return;
    }

    // Prompt for bot name
    const botName = window.prompt(t('list.botNamePrompt'));
    if (!botName || !botName.trim()) return;

    // LA CREACIÓN LA HACE EL SERVIDOR (D3a, 7-sep-2026). Antes esta pantalla
    // hacía `addDoc` directo, así que `canCreateBotLocally` —arriba— era la
    // ÚNICA puerta y corría en el navegador.
    //
    // Ese chequeo local se queda: no es la puerta, es lo que evita pedir el
    // nombre por `prompt` para luego rechazarlo, y lo que ofrece el enlace a la
    // tienda. La puerta está en el servidor, en transacción y contando
    // documentos.
    try {
      const idToken = await auth.currentUser.getIdToken();
      const payload = { nombre: botName.trim() };
      if (context.mode === 'organization' && context.orgId) {
        payload.organizationId = context.orgId;
      }
      const r = await fetch(`${FUNCTIONS_URL}/createBot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));

      if (!r.ok || !data.ok) {
        if (data.code === 'BOTS_LIMIT_REACHED') {
          // El servidor manda uso, tope y qué lo amplía. Se dice todo: un «no
          // puedes» sin el porqué es peor que no cortar.
          const msg = t('list.limitReached', { current: data.used, max: data.total });
          if (window.confirm(`${msg}\n\n${t('list.confirmUpgrade')}`)) navigate('/addon');
        } else {
          toast.error(data.message || t('list.errorCreate'));
        }
        return;
      }

      // El contador ya no se toca desde aquí: lo incrementa `createBot` en la
      // misma transacción que crea el bot. La pantalla escribía
      // `bots.length + 1`, un absoluto sobre el estado local.
      //
      // Y se navega al id QUE DEVUELVE EL SERVIDOR: sin él, el asistente queda
      // creado y sin poder configurarse.
      navigate(`/bot/${data.id}/configuracion`);
    } catch (error) {
      console.error('Error creating bot:', error);
      toast.error(t('list.errorCreate'));
    }
  };

  if (contextLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>{t('loadingContext')}</div>;
  }

  return (
    <div style={{ padding: 'clamp(12px, 3vw, 32px)', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box', width: '100%' }}>
      <PageHeader title={t('title')} />

      {/* Sección: Bots Avanzados */}
      <section>
        <h2 style={{
          borderBottom: '2px solid #9c27b0',
          paddingBottom: '0.5rem',
          marginBottom: '1rem'
        }}>
          {t('list.sectionTitle')}
        </h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          {t('list.sectionDescription')}
        </p>

        {canCreate && (
          <button
            onClick={crearBotAvanzado}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#9c27b0',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
              marginBottom: '1rem'
            }}
          >
            {t('list.createBot')}
          </button>
        )}
        {!canCreate && context.mode === 'organization' && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '6px',
            color: '#856404',
            marginBottom: '1rem'
          }}>
            {t('list.noPermissionCreate')}
          </div>
        )}

        {bots.length === 0 ? (
          <div style={{
            padding: '2rem',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p>{t('list.empty')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {bots.map((bot) => {
              const isConfigComplete = isBotConfigComplete(bot);
              const isActive = bot.autoReply !== false;
              const canActivate = isConfigComplete && !isActive;

              return (
                <div
                  key={bot.id}
                  style={{
                    padding: 'clamp(12px, 2vw, 24px)',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ margin: 0 }}>
                        {bot.config?.name || bot.nombre || t('list.unnamed')}
                      </h3>
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: isActive ? '#d4edda' : '#f8d7da',
                          color: isActive ? '#155724' : '#721c24'
                        }}
                      >
                        {isActive ? t('list.active') : t('list.inactive')}
                      </span>
                      {!isConfigComplete && (
                        <span
                          style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: '#fff3cd',
                            color: '#856404'
                          }}
                        >
                          {t('list.incompleteConfig')}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                      {bot.config?.purpose || bot.descripcion || t('list.noDescription')}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {isConfigComplete && (
                      <button
                        onClick={() => toggleBotAvanzado(bot.id, isActive)}
                        style={{
                          padding: '0.75rem 1rem',
                          backgroundColor: isActive ? '#ffc107' : '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 600
                        }}
                      >
                        {isActive ? t('list.pause') : t('list.activate')}
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/bot/${bot.id}/configuracion`)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#9c27b0',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      {t('list.configure')}
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => eliminarBot(bot.id)}
                        style={{
                          padding: '0.75rem 1rem',
                          backgroundColor: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Bots;